
import { useTranslation } from "react-i18next";

const WhyPartnerWithSmiklearn = () => {
  const { t } = useTranslation();

  return (
    <div className="bg-black/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Heading */}
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-10">
          {t("Why Partner with yslearn")}
        </h1>

        {/* Growing Demand Section */}
        <div className="mb-16">
          <h2 className="text-2xl font-semibold text-gray-800 mb-3">
            {t("Growing Demand For Upskilling")}
          </h2>
          <p className="text-gray-600 mb-8">
            {t(
              "The 4th Industrial Revolution is upon us and millions of people will require training within the next 10 years."
            )}
          </p>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Economic Growth Card */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex flex-col">
                <div className="flex items-baseline mb-2">
                  <span className="text-blue-500 text-4xl font-bold">
                    {t("$11.5T")}
                  </span>
                  <span className="ml-2 text-gray-600">
                    {t("Economic Growth")}
                  </span>
                </div>
                <p className="text-gray-500 text-sm">
                  {t(
                    "expected across 14 G20 countries by intelligent technologies"
                  )}
                </p>
              </div>
            </div>

            {/* Global Workforce Card */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex flex-col">
                <div className="flex items-baseline mb-2">
                  <span className="text-blue-500 text-4xl font-bold">
                    {t("90%")}
                  </span>
                  <span className="ml-2 text-gray-600">
                    {t("Global Workforce")}
                  </span>
                </div>
                <p className="text-gray-500 text-sm">
                  {t(
                    "that will potentially be impacted by intelligent technologies"
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Enabling Partner Success Section */}
        <div className="mb-16">
          <h2 className="text-2xl font-semibold text-gray-800 mb-3">
            {t("Enabling Partner Success")}
          </h2>
          <p className="text-gray-600 mb-8">
            {t(
              "yslearn is the World's #1 Online Bootcamp for digital skills. We scale our Enterprise business by driving success for our Partners."
            )}
          </p>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Online Bootcamp Card */}
            <div className="bg-blue-50 rounded-lg border border-blue-100 p-6">
              <div className="flex items-center mb-4">
                <div className="text-blue-500 mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800">
                  {t("Online Bootcamp Model Approach")}
                </h3>
              </div>
              <p className="text-gray-600 mb-4">
                {t(
                  "Our unique bootcamp format is designed to provide a better, more effective learning experience that includes:"
                )}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-gray-600 mr-2">•</span>
                  <span className="text-gray-600">
                    {t(
                      "Live instructor-led virtual classrooms & self-paced modules"
                    )}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-600 mr-2">•</span>
                  <span className="text-gray-600">
                    {t("High-engagement, outcome-centric training")}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-600 mr-2">•</span>
                  <span className="text-gray-600">
                    {t("Hands-on projects & cloud-based labs")}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-600 mr-2">•</span>
                  <span className="text-gray-600">
                    {t("Focus on Industry 4.0 digital skills")}
                  </span>
                </li>
              </ul>
            </div>

            {/* Dedicated Partner Support Card */}
            <div className="bg-blue-50 rounded-lg border border-blue-100 p-6">
              <div className="flex items-center mb-4">
                <div className="text-blue-500 mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800">
                  {t("Dedicated Partner Support")}
                </h3>
              </div>
              <p className="text-gray-600 mb-4">
                {t(
                  "At yslearn, we work hard to guarantee our partners' success by providing:"
                )}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-gray-600 mr-2">•</span>
                  <span className="text-gray-600">
                    {t("Dedicated partner portal for your customers")}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-600 mr-2">•</span>
                  <span className="text-gray-600">
                    {t("Sales cycle support")}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-600 mr-2">•</span>
                  <span className="text-gray-600">
                    {t("Marketing support")}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-600 mr-2">•</span>
                  <span className="text-gray-600">{t("Delivery support")}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Learn More Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col md:flex-row items-center justify-between">
          <div className="flex items-center mb-4 md:mb-0">
            <div className="text-blue-500 mr-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <span className="text-gray-800 font-medium">
              {t("Learn More About the yslearn Partner Program")}
            </span>
          </div>
          <a
            href="#"
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-6 rounded transition-colors"
          >
            {t("Download Brochure")}
          </a>
        </div>
      </div>
    </div>
  );
};

export default WhyPartnerWithSmiklearn;
